import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, CreditCard, Loader2, ShieldCheck } from "lucide-react";

export default function CardSetupReturn() {
  const [params] = useSearchParams();
  const apptId = params.get("appointment_id");
  const isMock = params.get("mock") === "1";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const tried = useRef(false);

  useEffect(() => {
    if (!apptId || tried.current) return;
    tried.current = true;
    let stop = false;
    let count = 0;
    const tick = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/appointments/${apptId}/card-setup/status`);
        setInfo(data);
        if (data.payment_status === "card_registered" || count > 8) return;
        count += 1;
        setTimeout(tick, 2000);
      } catch (e) {
        setError(formatErr(e?.response?.data?.detail) || e.message);
      }
    };
    tick();
    return () => { stop = true; };
  }, [apptId]);

  const done = info?.payment_status === "card_registered";
  const pending = !done && !error && (!info || info.payment_status === "card_required");
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md w-full text-center" data-testid="card-setup-return-card">
        {done ? <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto" /> : pending ? <Loader2 className="h-16 w-16 text-[#618DB8] mx-auto animate-spin" /> : <XCircle className="h-16 w-16 text-red-500 mx-auto" />}
        <h1 className="font-display text-2xl font-extrabold mt-4 text-deep" data-testid="card-setup-return-title">
          {done ? "Carte enregistrée" : pending ? "Vérification de votre carte…" : "Carte non enregistrée"}
        </h1>
        {done && (
          <>
            <p className="text-slate-600 mt-2">
              Votre téléconsultation avec <b>{info.doctor_name}</b> le <b>{new Date(info.start).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</b> est confirmée.
            </p>
            <div className="mt-4 text-sm bg-slate-50 rounded-lg p-4 border border-slate-200 text-left space-y-1.5" data-testid="card-setup-return-details">
              <div className="flex items-center gap-2 text-slate-700"><CreditCard className="h-4 w-4" />{(info.card_brand || "carte").toUpperCase()} •••• {info.card_last4}</div>
              <div className="text-slate-600">Aucun débit maintenant. <b>{info.fee_eur} €</b> seront prélevés automatiquement <b>à la fin de la téléconsultation</b>, directement au profit du praticien.</div>
              <div className="text-slate-500 text-xs">Annulation gratuite jusqu'à 24 h avant le rendez-vous. Au-delà, les honoraires peuvent être dus.</div>
              <div className="text-slate-500 text-xs flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />Paiement sécurisé Stripe · Doctobook ne conserve pas votre carte et ne perçoit aucune commission.</div>
              {(isMock || info.payment_mock) && <div className="text-amber-700 font-semibold text-xs" data-testid="card-setup-mock-badge">Mode démonstration — Stripe Connect non activé</div>}
            </div>
          </>
        )}
        {error && <div className="text-red-600 text-sm mt-3" data-testid="card-setup-return-error">{error}</div>}
        <Link to="/dashboard/appointments" className="block mt-6">
          <Button className="btn-primary" data-testid="card-setup-return-back">Voir mes rendez-vous</Button>
        </Link>
      </div>
    </div>
  );
}
